<footer class="bg-bg-base border-t border-border-strong text-text-secondary font-sans antialiased select-none">

  <!-- ── 1. LOADING STATE ── -->
  @if (isLoading) {
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex flex-col items-center justify-center text-center animate-pulse">
    <div class="w-10 h-10 rounded-xl bg-surface-elevated border border-border-strong flex items-center justify-center text-primary mb-4 shadow-inner-glow">
      <div class="loader"></div>
    </div>
    <span class="text-xs font-bold uppercase tracking-widest font-mono text-text-muted">
      {{ 'footer.loadingFooter' | translate }}
    </span>
  </div>
  }

  <!-- ── 2. ERROR STATE ── -->
  @if (errorMessage && !isLoading) {
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
    <div class="alert alert-danger max-w-md mx-auto items-center justify-center text-center rounded-2xl shadow-elevated">
      <i class="fa-solid fa-triangle-exclamation text-base"></i>
      <p class="text-xs font-mono font-bold tracking-tight m-0">{{ errorMessage }}</p>
    </div>
  </div>
  }

  <!-- ── 3. FOOTER CONTENT ── -->
  @if (footerData && !isLoading) {
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12 mb-12">

      <!-- Corporate Info Column -->
      <div class="space-y-5">
        <div class="inline-block">
          <img [src]="footerData.logo?.secure_url || 'logo.png'" (error)="$any($event.target).src='logo.avif'"
            [alt]="lang().startsWith('en') ? (footerData.title_en || footerData.title) : (footerData.title_ar || footerData.title)"
            class="h-16 object-contain filter brightness-100 antialiased" />
        </div>
        <p class="text-xs md:text-sm text-text-secondary font-medium leading-relaxed max-w-sm">
          {{ lang().startsWith('en') ? (footerData.description_en || footerData.description) :
          (footerData.description_ar || footerData.description) }}
        </p>

        <!-- Social Links -->
        <div class="flex items-center gap-2.5 pt-2">
          @for (social of footerData.socialLinks; track social._id) {
          <a [href]="social.link" target="_blank"
            class="w-9 h-9 bg-surface-elevated hover:bg-primary border border-border-strong hover:border-transparent text-text-secondary hover:text-white rounded-xl flex items-center justify-center transition-all duration-300 hover:shadow-glow hover:-translate-y-0.5">
            <i [class]="getSocialIcon(social.icon)" class="text-xs"></i>
          </a>
          }
        </div>
      </div>

      <!-- Quick Links Column -->
      <div>
        <div class="flex items-center gap-2 mb-5 pb-2 border-b border-border-subtle">
          <div class="w-1.5 h-3 bg-primary rounded-sm shadow-glow"></div>
          <h3 class="text-sm font-black text-text-primary uppercase tracking-wider font-mono">
            {{ 'footer.quickLinks' | translate }}
          </h3>
        </div>
        <ul class="grid grid-cols-1 gap-x-4 gap-y-3 text-xs md:text-sm">
          @for (link of footerData.quickLinks; track link._id || $index) {
          <li>
            <a [href]="link.link || link.url"
              class="text-text-secondary hover:text-text-primary transition-colors duration-200 flex items-center gap-2 group">
              <i [class]="getQuickLinkIcon((lang().startsWith('en') ? (link.title_en || link.title) : (link.title_ar || link.title)) || '')"
                class="text-[10px] text-text-muted group-hover:text-primary transition-colors w-4 shrink-0"></i>
              <span class="font-medium tracking-tight">
                {{ lang().startsWith('en') ? (link.title_en || link.title) : (link.title_ar || link.title) }}
              </span>
            </a>
          </li>
          }
        </ul>
      </div>

      <!-- Contact Column -->
      <div>
        <div class="flex items-center gap-2 mb-5 pb-2 border-b border-border-subtle">
          <div class="w-1.5 h-3 bg-primary rounded-sm shadow-glow"></div>
          <h3 class="text-sm font-black text-text-primary uppercase tracking-wider font-mono">
            {{ 'footer.contactUs' | translate }}
          </h3>
        </div>
        <ul class="space-y-3.5 text-xs md:text-sm">

          <!-- Email -->
          @if (footerData.contactInfo?.email) {
          <li class="flex items-center gap-3 group">
            <div
              class="w-7 h-7 bg-surface-elevated rounded-lg border border-border-strong flex items-center justify-center text-text-muted group-hover:text-primary group-hover:border-primary/50 transition-colors shadow-subtle">
              <i class="fa-regular fa-envelope text-[11px]"></i>
            </div>
            <span class="font-medium text-text-secondary group-hover:text-text-primary transition-colors tracking-tight select-all">{{ footerData.contactInfo?.email }}</span>
          </li>
          }

          <!-- Phones -->
          @for (phone of footerData.contactInfo?.phoneNumber; track $index) {
          <li class="flex items-center gap-3 group">
            <div
              class="w-7 h-7 bg-surface-elevated rounded-lg border border-border-strong flex items-center justify-center text-text-muted group-hover:text-primary group-hover:border-primary/50 transition-colors shadow-subtle">
              <i class="fa-solid fa-phone-volume text-[10px]"></i>
            </div>
            <span dir="ltr" class="font-mono font-bold text-text-secondary group-hover:text-text-primary transition-colors tracking-wide select-all">{{ phone }}</span>
          </li>
          }

          <!-- Address -->
          @if (footerData.contactInfo?.address_en || footerData.contactInfo?.address_ar ||
          footerData.contactInfo?.address) {
          <li class="flex items-start gap-3 group">
            <div
              class="w-7 h-7 bg-surface-elevated rounded-lg border border-border-strong flex items-center justify-center text-text-muted group-hover:text-primary group-hover:border-primary/50 transition-colors shrink-0 mt-0.5 shadow-subtle">
              <i class="fa-solid fa-map-pin text-[11px]"></i>
            </div>
            <span class="font-medium text-text-secondary group-hover:text-text-primary transition-colors leading-relaxed tracking-tight">
              {{ lang().startsWith('en') ? (footerData.contactInfo?.address_en || footerData.contactInfo?.address) :
              (footerData.contactInfo?.address_ar || footerData.contactInfo?.address) }}
            </span>
          </li>
          }
        </ul>
      </div>

    </div>

    <!-- ── 4. BOTTOM BAR ── -->
    <div class="border-t border-border-subtle pt-8 mt-8">
      <div class="flex flex-col md:flex-row items-center justify-between gap-5 text-xs text-text-muted font-medium">

        <!-- Copyright -->
        <div class="flex items-center gap-1.5 order-2 md:order-1 text-center md:text-left">
          <span class="font-mono text-[10px]">&copy;</span>
          <span class="tracking-tight text-text-secondary">
            {{ lang().startsWith('en') ? (footerData.copyright_en || footerData.copyright) : (footerData.copyright_ar ||
            footerData.copyright) }}
          </span>
        </div>

        <!-- Policies -->
        <div class="flex items-center gap-4 order-1 md:order-2">
          @for (policy of footerData.policies; track policy._id || $index) {
          <a [href]="policy.url"
            class="inline-flex items-center gap-2 px-3 py-1.5 bg-surface-elevated border border-border hover:border-primary text-text-secondary hover:text-text-primary rounded-xl transition-all duration-200 hover:shadow-subtle group">
            <i [class]="getPolicyIcon((lang().startsWith('en') ? (policy.label_en || policy.label) : (policy.label_ar || policy.label)) || '')"
              class="text-[10px] text-text-muted group-hover:text-primary transition-colors"></i>
            <span class="font-mono text-[11px] font-bold uppercase tracking-wider">
              {{ lang().startsWith('en') ? (policy.label_en || policy.label) : (policy.label_ar || policy.label) }}
            </span>
          </a>
          }
        </div>

      </div>
    </div>

  </div>
  }
</footer>